import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Filter, Calendar } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { getQuotationStats, getQuotations, getQuotationUserStats } from '../../services/taskApi';

const TIMEFRAMES = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'weekly', label: 'Last 7 Days' },
  { value: 'monthly', label: 'Last 30 Days' },
  { value: 'yearly', label: 'Last 1 Year' },
  { value: 'custom', label: 'Custom Range' },
];

const Reports = () => {
  const { isDark, user } = useOutletContext();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [userStats, setUserStats] = useState([]);
  const [recentConverted, setRecentConverted] = useState([]);
  const [timeframe, setTimeframe] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (timeframe === 'custom' && customFrom && customTo) {
        params.timeframe = 'custom';
        params.date_from = customFrom;
        params.date_to = customTo;
      } else if (timeframe && timeframe !== 'custom') {
        params.timeframe = timeframe;
      }
      const promises = [
        getQuotationStats(params),
        getQuotations({ status: 'converted' })
      ];
      if (isAdmin) promises.push(getQuotationUserStats(params));
      const results = await Promise.all(promises);
      setStats(results[0].data);
      setRecentConverted((results[1].data || []).slice(0, 10));
      if (isAdmin && results[2]) setUserStats(results[2].data || []);
    } catch (err) { console.error('Reports: fetch failed', err); }
  }, [timeframe, customFrom, customTo, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!stats) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  const cardCls = isDark ? 'border-0 bg-white/5' : 'border border-gray-200 bg-white shadow-sm';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white hover:bg-white/8' : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50';

  const funnelStages = [
    { label: 'Draft', count: stats.draft, color: 'bg-slate-500' },
    { label: 'Sent', count: stats.sent, color: 'bg-blue-500' },
    { label: 'Approved', count: stats.approved, color: 'bg-emerald-500' },
    { label: 'Converted', count: stats.converted, color: 'bg-green-500' },
    { label: 'Rejected', count: stats.rejected, color: 'bg-red-500' },
  ];
  const maxCount = Math.max(...funnelStages.map(s => s.count), 1);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Conversion Reports</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className={isDark ? 'text-white/30' : 'text-gray-400'} />
          <select
            className={`h-9 rounded-lg text-xs px-3 transition-colors border ${selectCls}`}
            value={timeframe}
            onChange={e => setTimeframe(e.target.value)}
            data-testid="report-timeframe-filter"
          >
            {TIMEFRAMES.map(tf => (
              <option key={tf.value} value={tf.value}>{tf.label}</option>
            ))}
          </select>
          {timeframe === 'custom' && (
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 rounded-lg px-2 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-300'}`}>
                <Calendar size={12} className={isDark ? 'text-white/30' : 'text-gray-400'} />
                <Input
                  type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className={`bg-transparent border-0 text-xs h-9 w-32 p-0 focus-visible:ring-0 ${isDark ? 'text-white' : 'text-gray-900'}`}
                  data-testid="report-date-from"
                />
              </div>
              <span className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>to</span>
              <div className={`flex items-center gap-1.5 rounded-lg px-2 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-300'}`}>
                <Calendar size={12} className={isDark ? 'text-white/30' : 'text-gray-400'} />
                <Input
                  type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className={`bg-transparent border-0 text-xs h-9 w-32 p-0 focus-visible:ring-0 ${isDark ? 'text-white' : 'text-gray-900'}`}
                  data-testid="report-date-to"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={cardCls}><CardContent className="p-4">
          <p className={`text-xs mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Total Quotations</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
        </CardContent></Card>
        <Card className={cardCls}><CardContent className="p-4">
          <p className={`text-xs mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Conversion Rate</p>
          <p className="text-2xl font-bold text-emerald-400">{stats.conversion_rate}%</p>
        </CardContent></Card>
        <Card className={cardCls}><CardContent className="p-4">
          <p className={`text-xs mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Pipeline Value</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Rs.{Math.round(stats.total_value).toLocaleString('en-IN')}</p>
        </CardContent></Card>
        <Card className={cardCls}><CardContent className="p-4">
          <p className={`text-xs mb-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Converted Value</p>
          <p className="text-2xl font-bold text-green-400">Rs.{Math.round(stats.converted_value).toLocaleString('en-IN')}</p>
        </CardContent></Card>
      </div>

      <Card className={cardCls}>
        <CardContent className="p-6">
          <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Quotation Pipeline</h3>
          <div className="space-y-3">
            {funnelStages.map(stage => (
              <div key={stage.label} className="flex items-center gap-3">
                <span className={`text-xs w-20 text-right ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{stage.label}</span>
                <div className={`flex-1 rounded-full h-8 overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <div
                    className={`${stage.color} h-full rounded-full flex items-center px-3 transition-all duration-500`}
                    style={{ width: `${Math.max((stage.count / maxCount) * 100, stage.count > 0 ? 15 : 0)}%` }}
                  >
                    <span className="text-xs font-bold text-white">{stage.count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className={cardCls}>
          <CardContent className="p-6">
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Submitted vs Converted</h3>
            <div className="flex items-end gap-6 h-40">
              <div className="flex-1 flex flex-col items-center justify-end">
                <div className="w-full bg-blue-500/20 rounded-t-lg flex items-end justify-center" style={{ height: `${Math.max(100, 20)}%` }}>
                  <span className="text-lg font-bold text-blue-400 pb-2">{stats.total}</span>
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Submitted</p>
              </div>
              <div className="flex-1 flex flex-col items-center justify-end">
                <div className="w-full bg-green-500/20 rounded-t-lg flex items-end justify-center" style={{ height: `${stats.total > 0 ? Math.max((stats.converted / stats.total) * 100, 10) : 10}%` }}>
                  <span className="text-lg font-bold text-green-400 pb-2">{stats.converted}</span>
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Converted</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardContent className="p-6">
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Recent Conversions</h3>
            {recentConverted.length === 0 ? (
              <div className={`text-center py-8 text-sm ${isDark ? 'text-white/30' : 'text-gray-400'}`}>No conversions yet</div>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {recentConverted.map(q => (
                  <div key={q.id} className={`flex items-center justify-between rounded-lg px-3 py-2 ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                    <div>
                      <p className="text-xs font-mono text-green-400">{q.quotation_number}</p>
                      <p className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{q.customer_name}</p>
                    </div>
                    <p className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Rs.{Math.round(q.total || 0).toLocaleString('en-IN')}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User-wise Quotation Report (Admin Only) */}
      {isAdmin && userStats.length > 0 && (
        <Card className={cardCls}>
          <CardContent className="p-6">
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>User-wise Quotation Report</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="user-stats-table">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <th className={`text-left py-2.5 px-3 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>User</th>
                    <th className={`text-center py-2.5 px-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Total</th>
                    <th className={`text-center py-2.5 px-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Draft</th>
                    <th className={`text-center py-2.5 px-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Sent</th>
                    <th className={`text-center py-2.5 px-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Approved</th>
                    <th className={`text-center py-2.5 px-2 text-xs font-medium uppercase tracking-wider text-green-400`}>Converted</th>
                    <th className={`text-center py-2.5 px-2 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Rejected</th>
                    <th className={`text-center py-2.5 px-2 text-xs font-medium uppercase tracking-wider text-emerald-400`}>Conv %</th>
                    <th className={`text-right py-2.5 px-3 text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Total Value</th>
                    <th className={`text-right py-2.5 px-3 text-xs font-medium uppercase tracking-wider text-green-400`}>Converted Value</th>
                  </tr>
                </thead>
                <tbody>
                  {userStats.map((u, i) => (
                    <tr key={u.user_id || i} className={`border-b ${isDark ? 'border-white/5 hover:bg-white/[0.03]' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}>
                      <td className={`py-2.5 px-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-900'}`}>{u.user_name}</td>
                      <td className={`py-2.5 px-2 text-center font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{u.total}</td>
                      <td className={`py-2.5 px-2 text-center ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{u.draft}</td>
                      <td className="py-2.5 px-2 text-center text-blue-400">{u.sent}</td>
                      <td className="py-2.5 px-2 text-center text-emerald-400">{u.approved}</td>
                      <td className="py-2.5 px-2 text-center font-bold text-green-400">{u.converted}</td>
                      <td className="py-2.5 px-2 text-center text-red-400">{u.rejected}</td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          u.conversion_rate >= 50 ? 'bg-green-500/15 text-green-400' :
                          u.conversion_rate >= 20 ? 'bg-yellow-500/15 text-yellow-400' :
                          'bg-red-500/15 text-red-400'
                        }`}>{u.conversion_rate}%</span>
                      </td>
                      <td className={`py-2.5 px-3 text-right ${isDark ? 'text-white/60' : 'text-gray-700'}`}>Rs.{Math.round(u.total_value).toLocaleString('en-IN')}</td>
                      <td className="py-2.5 px-3 text-right font-semibold text-green-400">Rs.{Math.round(u.converted_value).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className={`font-bold ${isDark ? 'border-t border-white/10 bg-white/[0.03]' : 'border-t-2 border-gray-300 bg-gray-50'}`}>
                    <td className={`py-2.5 px-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</td>
                    <td className={`py-2.5 px-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>{userStats.reduce((s, u) => s + u.total, 0)}</td>
                    <td className={`py-2.5 px-2 text-center ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{userStats.reduce((s, u) => s + u.draft, 0)}</td>
                    <td className="py-2.5 px-2 text-center text-blue-400">{userStats.reduce((s, u) => s + u.sent, 0)}</td>
                    <td className="py-2.5 px-2 text-center text-emerald-400">{userStats.reduce((s, u) => s + u.approved, 0)}</td>
                    <td className="py-2.5 px-2 text-center text-green-400">{userStats.reduce((s, u) => s + u.converted, 0)}</td>
                    <td className="py-2.5 px-2 text-center text-red-400">{userStats.reduce((s, u) => s + u.rejected, 0)}</td>
                    <td className="py-2.5 px-2 text-center">
                      {(() => { const t = userStats.reduce((s, u) => s + u.total, 0); const c = userStats.reduce((s, u) => s + u.converted, 0); return t > 0 ? `${(c / t * 100).toFixed(1)}%` : '0%'; })()}
                    </td>
                    <td className={`py-2.5 px-3 text-right ${isDark ? 'text-white' : 'text-gray-900'}`}>Rs.{Math.round(userStats.reduce((s, u) => s + u.total_value, 0)).toLocaleString('en-IN')}</td>
                    <td className="py-2.5 px-3 text-right text-green-400">Rs.{Math.round(userStats.reduce((s, u) => s + u.converted_value, 0)).toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
